import axios from "axios";

import { getCurrentLanguage } from "@/core/i18n";
import { Analytics, AnalyticsEvents } from "modules/analytics";

import { get as apiGet, post as apiPost, getApiBaseUrl } from "../../api/index";

import { AdonisIdentity, AdonisRecommendationModel, DEFAULT_ADONIS_IDENTITY } from "./adonis.types";
import { AdonisRecommendation } from "./IAdonisRecommendation";
import { AnthropicStreamHandler } from "./streaming/anthropic-stream-handler";

const warmupCache = new Map<string, Promise<boolean>>();

export const warmAdonisCache = async (sessionId: string): Promise<boolean> => {
  if (warmupCache.has(sessionId)) {
    const existingPromise = warmupCache.get(sessionId);
    if (existingPromise) {
      return existingPromise;
    }
  }

  const warmupPromise = (async () => {
    try {
      const response = await apiPost(
        "/adonis/warm",
        { sessionId },
        undefined,
        // Must match the header createStreamingResponse sends. The warm-up primes an Anthropic
        // prompt cache keyed on the prompt prefix, and the language instruction sits inside that
        // prefix — a warm-up sent without it caches something the recommendation cannot reuse.
        { "X-Preferred-Language": getCurrentLanguage() },
      );

      if (!response) {
        throw new Error(`Warmup failed`);
      }

      return response ?? false;
    } catch (error) {
      console.error("Error warming cache:", error);
      return false;
    } finally {
      warmupCache.delete(sessionId);
    }
  })();

  warmupCache.set(sessionId, warmupPromise);

  return warmupPromise;
};

export const streamWithHandler = async (
  sessionId: string,
  recommendation: string,
  handler: AnthropicStreamHandler,
): Promise<void> => {
  try {
    const response = await createStreamingResponse(sessionId, recommendation);
    if (!response.body) {
      throw new Error("ReadableStream not supported");
    }
    await handler.processStream(response.body);
  } catch (error) {
    console.error("Error creating recommendation with SSE:", error);
    throw error;
  }
};

// New function for the adapter integration
export const createStreamingResponse = async (
  sessionId: string,
  recommendation: string,
): Promise<Response> => {
  const baseUrl = getApiBaseUrl();

  // Use fetch for SSE
  const authHeader = axios.defaults.headers.common["Authorization"] as string;
  const response = await fetch(`${baseUrl}/api/v1/adonis/recommendation`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "text/event-stream",
      "Cache-Control": "no-cache",
      // Tells Adonis what language to respond in. The backend has no other way to know
      // this: the patient app's language is chosen and persisted entirely client-side
      // (see core/i18n), so it must be sent per-request.
      "X-Preferred-Language": getCurrentLanguage(),
      ...(authHeader ? { Authorization: authHeader } : {}),
    },
    body: JSON.stringify({ sessionId, recommendation }),
  });

  if (!response.ok) {
    throw new Error(`API returned status ${response.status}`);
  }

  if (!response.body) {
    throw new Error("ReadableStream not supported");
  }

  return response;
};

export const getAdonisRecommendationsBySession = async (
  sessionId: string,
): Promise<AdonisRecommendation[]> => {
  try {
    const id = sessionId;
    const recommendations = await apiGet("/adonis/recommendations/session/{id}", {
      id,
    });
    if (!recommendations) {
      console.error("No response from API");
      return [];
    }

    return recommendations.map((msg: AdonisRecommendationModel) => ({
      role: msg.userQuery ? "user" : "assistant",
      userQuery: msg.userQuery,
      adonisResponse: msg.adonisResponse,
      createdAt: msg.createdAt,
      updatedAt: msg.updatedAt,
    }));
  } catch (error) {
    console.error("Error getting recommendations:", error);
    return [];
  }
};

export const getAdonisStarterQuestions = async (): Promise<{ id: string; text: string }[]> => {
  try {
    const response = await apiGet("/adonis/starter-questions");

    if (!response || !Array.isArray(response)) {
      console.error("Invalid response from starter questions API");
      return [];
    }

    return response
      .filter((question: string) => question && question.trim())
      .map((question: string, index: number) => ({
        id: (index + 1).toString(),
        text: question.trim(),
      }));
  } catch (error) {
    console.error("Error getting starter questions:", error);
    return [];
  }
};

const trackAdonisLinkClicked = (): void => {
  Analytics.track(AnalyticsEvents.ADONIS_LINK_CLICKED, {});
};

export const createPdpLinkHandler = (navigate: (path: string) => void) => {
  return (href: string) => {
    const linkId = `link-${crypto.randomUUID()}`;

    if (typeof window !== "undefined") {
      // @ts-expect-error - pdpNavigate is not defined in the window object
      window.pdpNavigate = window.pdpNavigate ?? {};
      // @ts-expect-error - pdpNavigate is not defined in the window object
      window.pdpNavigate[linkId] = () => {
        trackAdonisLinkClicked();
        return navigate(href);
      };
    }

    return linkId;
  };
};

const toTitleCase = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Normalizes a persona identity, ensuring it always returns a valid AdonisIdentity.
 * If the provided identity is undefined, null, or missing required fields,
 * returns the default Adonis identity.
 *
 * @param identity - Optional partial or complete AdonisIdentity
 * @returns A complete AdonisIdentity, defaulting to Adonis if input is invalid
 */
export const normalizePersona = (identity?: Partial<AdonisIdentity> | null): AdonisIdentity => {
  if (!identity || !identity.name || !identity.pronoun || !identity.objectPronoun) {
    console.warn("Invalid or missing AI persona identity, using default Adonis identity", identity);
    return DEFAULT_ADONIS_IDENTITY;
  }
  return {
    name: identity.name,
    pronoun: identity.pronoun,
    objectPronoun: identity.objectPronoun,
  };
};

export const replacePersonaPlaceholders = (
  text: string,
  identity?: Partial<AdonisIdentity> | null,
): string => {
  if (!text) return text;

  // Normalize identity to ensure we always have valid values (defaults to Adonis)
  const normalizedIdentity = normalizePersona(identity);

  return text
    .replace(/{NAME}/g, normalizedIdentity.name.toUpperCase())
    .replace(/{Name}/g, toTitleCase(normalizedIdentity.name))
    .replace(/{name}/g, normalizedIdentity.name.toLowerCase())
    .replace(/{PRONOUN}/g, normalizedIdentity.pronoun.toUpperCase())
    .replace(/{Pronoun}/g, toTitleCase(normalizedIdentity.pronoun))
    .replace(/{pronoun}/g, normalizedIdentity.pronoun.toLowerCase())
    .replace(/{OBJECTPRONOUN}/g, normalizedIdentity.objectPronoun.toUpperCase())
    .replace(/{ObjectPronoun}/g, toTitleCase(normalizedIdentity.objectPronoun))
    .replace(/{objectPronoun}/g, normalizedIdentity.objectPronoun.toLowerCase());
};
